import React, { useState } from "react";
import { I18n, ConsoleLogger as Logger } from "@aws-amplify/core";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { withStyles } from "@material-ui/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import InputLabel from "@material-ui/core/InputLabel";
import Link from "@material-ui/core/Link";

import Dict from "../../resources/Dict";
import CustomInputAuth from "./CustomInputAuth";
import IncorrectInputMessage from "../Form/IncorrectInputMessage";
import colors from "../../assets/style/colors";

I18n.setLanguage("en");
I18n.putVocabularies(Dict);

const logger = new Logger("ConfirmSignIn");

const styles = {
  text: {
    fontSize: "14px",
    color: colors.black87,
  },
};

const CustomConfirmSignIn = (props) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [incorrectForm, setIncorrectForm] = useState(null);

  const resendCode = async () => {
    const { username } = props;
    if (!username) {
      logger.debug("username is missing");

      return;
    }

    setLoading(true);
    try {
      await signIn({
        username,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "EMAIL_OTP",
        },
      });
      setIncorrectForm("codeResent");
    } catch (error) {
      logger.debug("resendCode error", error);

      setIncorrectForm("resendFailed");
    } finally {
      setLoading(false);
    }
  };

  const confirm = async (event) => {
    if (event) {
      event.preventDefault();
    }

    if (!code) {
      setIncorrectForm("emptyCode");

      return;
    }

    setLoading(true);
    setIncorrectForm(null);
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: code,
      });

      if (nextStep?.signInStep === "DONE") {
        props.onAuthStateChange?.("signedIn");

        return;
      }

      // If Cognito/Amplify indicates another step, keep user on this screen.
      setIncorrectForm("incorrectCode");
    } catch (error) {
      if (
        error &&
        (error.code === "CodeMismatchException" || error.code === "ExpiredCodeException")
      ) {
        setIncorrectForm("incorrectCode");

        return;
      }

      logger.debug("confirmSignIn error", error);
      setIncorrectForm("incorrectCode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="div">
      <Typography
        className={"text-87"}
        variant="h3"
        component="h3"
        align="center"
      >
        {I18n.get("auth_otpTitle")}
      </Typography>
      <Typography className={props.classes.text} component="p">
        {I18n.get("auth_otpDesc")}
      </Typography>
      <form onSubmit={confirm} noValidate>
        <InputLabel htmlFor="code" className={props.classes.text}>
          {I18n.get("auth_verificationCode")}
        </InputLabel>
        <CustomInputAuth
          key="code"
          name="code"
          type="number"
          id="code"
          autoFocus
          onChange={(e) => setCode(e.target.value)}
          value={code}
        />
        {incorrectForm === "emptyCode" && (
          <IncorrectInputMessage
            message={I18n.get("auth_pleaseEnterAPassword")}
          />
        )}
        {incorrectForm === "incorrectCode" && (
          <IncorrectInputMessage
            message={I18n.get("auth_invalidVerificationCode")}
          />
        )}
        {incorrectForm === "codeResent" && (
          <p style={{ color: colors.green, margin: 0, fontSize: "0.875em", textAlign: "center" }}>
            {I18n.get("auth_codeResent")}
          </p>
        )}
        {incorrectForm === "resendFailed" && (
          <IncorrectInputMessage
            message={I18n.get("error")}
          />
        )}
        <Box style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
          <Link
            variant="inherit"
            onClick={() => props.onAuthStateChange?.("signIn")}
            style={{ cursor: "pointer" }}
          >
            {I18n.get("auth_backToSignIn")}
          </Link>
          <Link
            variant="inherit"
            onClick={resendCode}
            style={{ cursor: "pointer" }}
          >
            {I18n.get("auth_resendCode")}
          </Link>
        </Box>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          disabled={loading}
          style={{ marginTop: "20px" }}
        >
          {I18n.get("auth_submit")}
        </Button>
      </form>
    </Box>
  );
};

export default withStyles(styles)(CustomConfirmSignIn);
